// entityTimeline — the pure read model for one resource's own history in the
// Journal (its mini-timeline): the server-folded moments (loadEntityHistory)
// as day-grouped, freshness-tiered rows, newest first.
//
// Pure and deterministic given its inputs: `nowMs` (the wall clock) and
// `locale` are threaded IN by the host, and the per-moment TITLE is threaded as
// `momentTitle`, so this projection stays Date-free at the call site and
// Lingui-free here.

import type {
    ActivityRow,
    DayPeriod,
    FreshnessTier,
    TimelineGroup,
} from "../components/WorkspaceHome/JournalTimeline.js";
import type { EntityHistoryMoment } from "./ports.js";
import { formatRecency } from "./recency.js";

const DAY = 86_400_000;
// The perishable-information thresholds (project_reviewability_core_value):
// under three days reads as current, under three weeks as aging, older (or an
// unknown instant) as stale.
const FRESH_MAX = 3 * DAY;
const AGING_MAX = 21 * DAY;

const freshnessOf = (at: string, nowMs: number): FreshnessTier => {
    const then = Date.parse(at);
    if (Number.isNaN(then)) return "stale";
    const age = nowMs - then;
    return age < FRESH_MAX ? "fresh" : age < AGING_MAX ? "aging" : "stale";
};

// The local-day boundary containing `nowMs` (the runtime timezone, the same
// convention `recency.ts` uses).
const startOfLocalDay = (nowMs: number): number => {
    const d = new Date(nowMs);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
};

// Which day bucket an instant falls in; an unparseable one is "earlier".
const periodOf = (at: string, todayStart: number): DayPeriod => {
    const then = Date.parse(at);
    if (Number.isNaN(then)) return "earlier";
    if (then >= todayStart) return "today";
    if (then >= todayStart - DAY) return "yesterday";
    return "earlier";
};

const PERIOD_ORDER: readonly DayPeriod[] = ["today", "yesterday", "earlier"];

// The row kind (hence icon) a moment renders as: an ingest reads as a source,
// a packaging as a package; everything else acts on a material's lifecycle.
const iconKindOf = (kind: EntityHistoryMoment["kind"]): ActivityRow["type"] => {
    switch (kind) {
        case "ingested_source":
            return "source";
        case "authored_material":
        case "edited_material":
        case "version_restored":
        case "deleted_resource":
        case "restored_resource":
            return "material";
        case "packaged_material":
            return "package";
    }
};

// The row variants are structurally identical, so a switch picks the concrete
// discriminant without a cast.
const entityRow = (
    type: ActivityRow["type"],
    base: Omit<ActivityRow, "type">,
): ActivityRow => {
    switch (type) {
        case "source":
            return { type: "source", ...base };
        case "material":
            return { type: "material", ...base };
        case "package":
            return { type: "package", ...base };
        case "conversation":
            return { type: "conversation", ...base };
    }
};

export const buildEntityTimeline = (params: {
    readonly moments: readonly EntityHistoryMoment[];
    readonly nowMs: number;
    readonly locale: string;
    // The localized title for a moment ("Created", "Edited (3 changes)", …).
    readonly momentTitle: (moment: EntityHistoryMoment) => string;
}): readonly TimelineGroup[] => {
    const { moments, nowMs, locale, momentTitle } = params;
    const todayStart = startOfLocalDay(nowMs);
    // Server folds oldest→newest; the Journal reads newest-first. ISO 8601 UTC
    // strings compare chronologically.
    const rows = [...moments]
        .sort((a, b) => b.at.localeCompare(a.at))
        .map((moment) => {
            const recency = formatRecency(moment.at, nowMs, locale);
            return {
                at: moment.at,
                row: entityRow(iconKindOf(moment.kind), {
                    id: moment.eventId,
                    title: momentTitle(moment),
                    when: recency?.relative ?? "",
                    whenAbsolute: recency?.absolute ?? "",
                    at: moment.at,
                    freshness: freshnessOf(moment.at, nowMs),
                }),
            };
        });
    return PERIOD_ORDER.map((period) => ({
        period,
        entries: rows
            .filter((r) => periodOf(r.at, todayStart) === period)
            .map((r) => r.row),
    })).filter((group) => group.entries.length > 0);
};
